import React, { useMemo, useState } from 'react';
import { fmtDateDayMonth, fmtWeekdayShort } from '../utils/dateUtils';
import useIsMobile from '../hooks/useIsMobile';

// ── Helpers ─────────────────────────────────────────────────────────────────
const fmtMoney = (n) =>
  '₹\u202f' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const pctChange = (today, yesterday) => {
  if (!yesterday || yesterday === 0) return null;
  return ((today - yesterday) / yesterday) * 100;
};

const PAYMENT_COLOURS = {
  Cash: '#22c55e',
  Card: '#3b82f6',
  UPI: '#a855f7',
  Online: '#06b6d4',
  Unknown: 'var(--theme-text-muted)'
};
const payColour = (pt) => PAYMENT_COLOURS[pt] || '#f59e0b';

const HOURS_LABEL = (h) => {
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}${ampm}`;
};

// ── Shared style primitives ──────────────────────────────────────────────────
const card = {
  background: '#fff',
  border: '1px solid var(--theme-border)',
  borderRadius: '12px',
  padding: '1rem 1.2rem',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
};

const secHead = {
  margin: '0 0 0.75rem',
  fontSize: '0.9rem',
  fontWeight: 700,
  color: 'var(--theme-primary-dark)',
  paddingBottom: '0.4rem',
  borderBottom: '2px solid var(--theme-border)'
};

// ── Mini bar chart (reusable) ────────────────────────────────────────────────
function MiniBars({ rows, valueKey, labelKey, colour = '#3b82f6', height = 90 }) {
  const max = Math.max(...rows.map((r) => Number(r[valueKey] || 0)), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: `${height}px` }}>
      {rows.map((row, i) => {
        const val = Number(row[valueKey] || 0);
        const h = Math.max(2, Math.round((val / max) * (height - 16)));
        return (
          <div key={i} style={{ flex: 1, minWidth: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            <div
              title={`${row[labelKey]}: ${fmtMoney(val)}`}
              style={{ width: '100%', height: `${h}px`, background: typeof colour === 'function' ? colour(row) : colour, borderRadius: '3px 3px 0 0', cursor: 'default', transition: 'height 0.3s' }}
            />
            {row[labelKey] !== undefined && (
              <div style={{ fontSize: '0.55rem', color: 'var(--theme-text-muted)', textAlign: 'center', lineHeight: 1 }}>
                {String(row[labelKey])}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────────────────
function Dashboard({ stats }) {
  const { isMobile } = useIsMobile();
  const [histView, setHistView] = useState('28d'); // '28d' | '7d'

  // Always call hooks in the same order
  const history = stats?.forecast?.history || [];
  const displayHistory = useMemo(() => {
    if (!stats) return [];
    if (histView === '7d') return history.slice(-7);
    return history;
  }, [stats, history, histView]);

  if (!stats) {
    return (
      <div style={{ padding: '2rem', color: 'var(--theme-text-secondary)', fontFamily: 'system-ui, sans-serif' }}>
        <h2 style={{ color: 'var(--theme-primary-dark)' }}>Bar Dashboard</h2>
        <p>Loading…</p>
      </div>
    );
  }

  // ── Derived values ──────────────────────────────────────────────────────
  const todayRev = Number(stats.todayOrderAmount || 0);
  const yesterdayRev = Number(stats.yesterdayAmount || 0);
  const mtdRev = Number(stats.mtdAmount || 0);
  const todayCount = Number(stats.todayOrdersCount || 0);
  const avgOrderVal = todayCount > 0 ? todayRev / todayCount : 0;
  const dayPct = pctChange(todayRev, yesterdayRev);

  const bsMap = stats.billStatusBreakdown || {};
  const settledCount = bsMap['SETTLED']?.count || 0;
  const settledAmt = bsMap['SETTLED']?.amount || 0;
  const openCount = bsMap['OPEN']?.count || 0;
  const voidCount = (bsMap['VOID']?.count || 0) + (bsMap['VOIDED']?.count || 0);
  const voidRate = todayCount > 0 ? ((voidCount / todayCount) * 100).toFixed(1) : '0.0';

  const payBreak = Array.isArray(stats.paymentBreakdown) ? stats.paymentBreakdown : [];
  const payTotal = payBreak.reduce((s, r) => s + Number(r.amount || 0), 0);

  const hourly = Array.isArray(stats.hourlyToday) ? stats.hourlyToday : [];
  const maxHourlyCount = Math.max(...hourly.map((r) => r.order_count), 1);

  const topItems = Array.isArray(stats.topItemsToday) ? stats.topItemsToday : [];
  const maxTopQty = Math.max(...topItems.map((r) => r.total_qty), 1);

  const forecastDaily = stats.forecast?.daily || [];
  const maxForecast = Math.max(...forecastDaily.map((r) => Number(r.predicted_amount || 0)), 1);

  const maxHistAmt = Math.max(...displayHistory.map((r) => Number(r.amount || 0)), 1);

  const lowStock = Array.isArray(stats.lowStock) ? stats.lowStock : [];
  const tableWise = Array.isArray(stats.tableWiseTodayOrderAmount) ? stats.tableWiseTodayOrderAmount : [];

  return (
    <div style={{ padding: '1.5rem', background: 'var(--theme-bg-light)', minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ margin: 0, color: '#1e293b', fontSize: '1.35rem', fontWeight: 800 }}>Bar Dashboard</h2>
        <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.8rem', marginTop: '0.2rem' }}>
          Today's operations &amp; revenue overview &nbsp;·&nbsp; Auto-refreshes on order activity
        </div>
      </div>

      {/* ── Row 1: Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.85rem', marginBottom: '1.1rem' }}>

        {/* Today Revenue */}
        <div style={{ ...card, borderLeft: '4px solid var(--theme-primary-dark)' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Today's Revenue</div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e293b', marginTop: '0.25rem' }}>{fmtMoney(todayRev)}</div>
          {dayPct !== null && (
            <div style={{ fontSize: '0.72rem', marginTop: '0.2rem', color: dayPct >= 0 ? 'var(--theme-success)' : 'var(--theme-danger)', fontWeight: 600 }}>
              {dayPct >= 0 ? '▲' : '▼'} {Math.abs(dayPct).toFixed(1)}% vs yesterday
            </div>
          )}
          {dayPct === null && (
            <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: '0.2rem' }}>Yesterday: {fmtMoney(yesterdayRev)}</div>
          )}
        </div>

        {/* MTD */}
        <div style={{ ...card, borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>MTD Revenue</div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e293b', marginTop: '0.25rem' }}>{fmtMoney(mtdRev)}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: '0.2rem' }}>Month to date</div>
        </div>

        {/* Today Orders */}
        <div style={{ ...card, borderLeft: '4px solid #a855f7' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Today's Orders</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e293b', marginTop: '0.25rem' }}>{fmtNum(todayCount)}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: '0.2rem' }}>
            Settled {fmtNum(settledCount)} &nbsp;·&nbsp; Open {fmtNum(openCount)}
          </div>
        </div>

        {/* Avg Order Value */}
        <div style={{ ...card, borderLeft: '4px solid #06b6d4' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Avg Order Value</div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e293b', marginTop: '0.25rem' }}>{fmtMoney(avgOrderVal)}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: '0.2rem' }}>Per order today</div>
        </div>

        {/* Settled Today */}
        <div style={{ ...card, borderLeft: '4px solid #22c55e' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Settled Today</div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#15803d', marginTop: '0.25rem' }}>{fmtMoney(settledAmt)}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: '0.2rem' }}>{fmtNum(settledCount)} bills settled</div>
        </div>

        {/* Void Rate */}
        <div style={{ ...card, borderLeft: voidCount > 0 ? '4px solid #ef4444' : '4px solid var(--theme-border)' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Void Rate</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: voidCount > 0 ? 'var(--theme-danger)' : '#15803d', marginTop: '0.25rem' }}>{voidRate}%</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: '0.2rem' }}>{fmtNum(voidCount)} voids today</div>
        </div>

        {/* Low Stock */}
        <div style={{ ...card, borderLeft: lowStock.length > 0 ? '4px solid #f59e0b' : '4px solid var(--theme-border)' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Low Stock</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: lowStock.length > 0 ? 'var(--theme-warning)' : '#15803d', marginTop: '0.25rem' }}>{fmtNum(lowStock.length)}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: '0.2rem' }}>Items at/below reorder</div>
        </div>
      </div>

      {/* ── Row 2: Payment Breakdown + Bill Status ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,3fr) minmax(0,2fr)', gap: '1rem', marginBottom: '1.1rem', alignItems: 'start' }}>

        {/* Payment Breakdown */}
        <div style={card}>
          <h3 style={secHead}>Payment Method Breakdown (Today — Settled)</h3>
          {payBreak.length === 0 ? (
            <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No settled orders today.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              {payBreak.map((row) => {
                const pct = payTotal > 0 ? (Number(row.amount) / payTotal) * 100 : 0;
                return (
                  <div key={row.payment_type}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: payColour(row.payment_type), display: 'inline-block' }} />
                        {row.payment_type}
                        <span style={{ color: 'var(--theme-text-muted)', fontWeight: 400 }}>({fmtNum(row.order_count)} orders)</span>
                      </span>
                      <span>{fmtMoney(row.amount)} &nbsp;<span style={{ color: 'var(--theme-text-muted)', fontSize: '0.72rem' }}>{pct.toFixed(1)}%</span></span>
                    </div>
                    <div style={{ background: 'var(--theme-border)', borderRadius: '99px', height: '7px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: payColour(row.payment_type), borderRadius: '99px', transition: 'width 0.4s ease' }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: '0.73rem', color: 'var(--theme-text-secondary)', textAlign: 'right', marginTop: '0.2rem' }}>
                Total settled: {fmtMoney(payTotal)}
              </div>
            </div>
          )}
        </div>

        {/* Bill Status */}
        <div style={card}>
          <h3 style={secHead}>Order Status (Today)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {[
              { key: 'SETTLED', label: 'Settled', colour: '#22c55e', bg: '#dcfce7' },
              { key: 'OPEN', label: 'Open / Pending', colour: '#f59e0b', bg: '#fef3c7' },
              { key: 'VOID', label: 'Voided', colour: '#ef4444', bg: '#fee2e2' }
            ].map(({ key, label, colour, bg }) => {
              const voideKey2 = key === 'VOID' ? (bsMap['VOID'] || bsMap['VOIDED']) : bsMap[key];
              const count = voideKey2?.count || 0;
              const amt = voideKey2?.amount || 0;
              return (
                <div key={key} style={{ background: bg, borderRadius: '8px', padding: '0.6rem 0.8rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.72rem', color: colour, fontWeight: 700 }}>{label}</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 800, color: colour }}>{fmtNum(count)} orders</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colour }}>{fmtMoney(amt)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Row 3: Hourly Heatmap ── */}
      <div style={{ ...card, marginBottom: '1.1rem' }}>
        <h3 style={secHead}>Hourly Order Activity (Today)</h3>
        {hourly.every((r) => r.order_count === 0) ? (
          <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No orders yet today.</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '80px', overflowX: 'auto' }}>
              {hourly.map((row) => {
                const intensity = maxHourlyCount > 0 ? row.order_count / maxHourlyCount : 0;
                const bg = row.order_count === 0 ? 'var(--theme-border-light)' : `rgba(39,60,117,${0.15 + intensity * 0.85})`;
                const h = Math.max(8, Math.round(intensity * 64));
                return (
                  <div key={row.hour} style={{ flex: 1, minWidth: '22px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                    <div title={`${HOURS_LABEL(row.hour)}: ${row.order_count} orders — ${fmtMoney(row.amount)}`}
                      style={{ width: '100%', height: `${h}px`, background: bg, borderRadius: '3px 3px 0 0', cursor: 'default' }} />
                    {row.order_count > 0 && (
                      <div style={{ fontSize: '0.6rem', color: 'var(--theme-primary-dark)', fontWeight: 600, lineHeight: 1 }}>{row.order_count}</div>
                    )}
                    <div style={{ fontSize: '0.55rem', color: 'var(--theme-text-muted)', lineHeight: 1 }}>{HOURS_LABEL(row.hour)}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--theme-text-muted)', marginTop: '0.4rem', textAlign: 'right' }}>
              Darker = more orders &nbsp;·&nbsp; Peak hour: {HOURS_LABEL(hourly.reduce((best, r) => r.order_count > (hourly[best]?.order_count || 0) ? r.hour : best, 0))}
            </div>
          </>
        )}
      </div>

      {/* ── Row 4: 28-day History + Forecast ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(0,1fr)', gap: '1rem', marginBottom: '1.1rem', alignItems: 'start' }}>

        {/* 28-day History */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h3 style={{ ...secHead, margin: 0, border: 'none', paddingBottom: 0 }}>Revenue History</h3>
            <div style={{ display: 'flex', gap: '0.3rem' }}>
              {['7d', '28d'].map((v) => (
                <button key={v} type="button" onClick={() => setHistView(v)}
                  style={{ padding: '0.2rem 0.55rem', border: histView === v ? '2px solid var(--theme-primary-dark)' : '1.5px solid var(--theme-border)', borderRadius: '999px', background: histView === v ? '#eef4ff' : 'var(--theme-bg-light)', color: histView === v ? 'var(--theme-primary-dark)' : 'var(--theme-text-secondary)', cursor: 'pointer', fontSize: '0.7rem', fontWeight: histView === v ? 700 : 500 }}>
                  {v === '7d' ? 'Last 7' : 'Last 28'}
                </button>
              ))}
            </div>
          </div>
          <div style={{ borderBottom: '2px solid var(--theme-border)', marginBottom: '0.75rem' }} />
          {displayHistory.length === 0 ? (
            <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No history data.</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '100px' }}>
                {displayHistory.map((row, i) => {
                  const val = Number(row.amount || 0);
                  const h = Math.max(2, Math.round((val / maxHistAmt) * 84));
                  const isToday = i === displayHistory.length - 1;
                  return (
                    <div key={i} style={{ flex: 1, minWidth: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                      <div title={`${fmtDateDayMonth(row.date)}: ${fmtMoney(val)}`}
                        style={{ width: '100%', height: `${h}px`, background: isToday ? 'var(--theme-primary-dark)' : '#93c5fd', borderRadius: '3px 3px 0 0', cursor: 'default' }} />
                      {(i === 0 || i === Math.floor(displayHistory.length / 2) || isToday) && (
                        <div style={{ fontSize: '0.52rem', color: 'var(--theme-text-muted)', textAlign: 'center', lineHeight: 1 }}>
                          {fmtDateDayMonth(row.date)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--theme-text-muted)', textAlign: 'right', marginTop: '0.3rem' }}>
                Peak: {fmtMoney(maxHistAmt)} &nbsp;·&nbsp; <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>■ Today</span>
              </div>
            </>
          )}
        </div>

        {/* 7-day Forecast */}
        <div style={card}>
          <h3 style={secHead}>Demand Forecast — Next 7 Days</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '0.55rem 0.8rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', fontWeight: 600 }}>Forecast Avg / Day</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0ea5e9' }}>{fmtMoney(stats.forecast?.avgPerDay || 0)}</div>
            </div>
            <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '0.55rem 0.8rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', fontWeight: 600 }}>7-Day Total</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--theme-success)' }}>{fmtMoney(stats.forecast?.next7DaysAmount || 0)}</div>
            </div>
          </div>
          {forecastDaily.length === 0 ? (
            <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No forecast data.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '6px', height: '100px' }}>
              {forecastDaily.map((row) => {
                const val = Number(row.predicted_amount || 0);
                const h = Math.max(8, Math.round((val / maxForecast) * 84));
                return (
                  <div key={row.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div style={{ fontSize: '0.6rem', color: '#475569', fontWeight: 600 }}>{fmtMoney(val).replace('₹\u202f', '').replace(/,/g, '')}</div>
                    <div title={`${fmtDateDayMonth(row.date)}: ${fmtMoney(val)}`}
                      style={{ width: '100%', maxWidth: '36px', height: `${h}px`, background: 'linear-gradient(180deg, #60a5fa 0%, #2563eb 100%)', borderRadius: '5px 5px 2px 2px' }} />
                    <div style={{ fontSize: '0.6rem', color: 'var(--theme-text-muted)' }}>
                      {fmtWeekdayShort(row.date)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: '0.65rem', color: 'var(--theme-text-muted)', marginTop: '0.5rem' }}>
            Based on {stats.forecast?.lookbackDays}-day weekday average model
          </div>
        </div>
      </div>

      {/* ── Row 5: Top Selling Items + Table-wise ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(0,1fr)', gap: '1rem', marginBottom: '1.1rem', alignItems: 'start' }}>

        {/* Top Selling Items Today */}
        <div style={card}>
          <h3 style={secHead}>Top Selling Items (Today)</h3>
          {topItems.length === 0 ? (
            <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No item data for today.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
              {topItems.map((item, i) => {
                const pct = maxTopQty > 0 ? (item.total_qty / maxTopQty) * 100 : 0;
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.2rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span style={{ background: '#e0e7ff', color: '#4338ca', borderRadius: '4px', padding: '0.05rem 0.35rem', fontSize: '0.65rem', fontWeight: 700, minWidth: '16px', textAlign: 'center' }}>{i + 1}</span>
                        <span style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.item_name}</span>
                      </span>
                      <span style={{ color: '#475569' }}>{fmtNum(item.total_qty)} qty &nbsp;·&nbsp; {fmtMoney(item.total_amount)}</span>
                    </div>
                    <div style={{ background: 'var(--theme-border)', borderRadius: '99px', height: '5px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: i === 0 ? '#7c3aed' : '#a78bfa', borderRadius: '99px' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Table-wise Activity */}
        <div style={card}>
          <h3 style={secHead}>Table Activity (Today)</h3>
          {tableWise.length === 0 ? (
            <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No table orders today.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: '360px' }}>
                <thead>
                  <tr style={{ background: 'var(--theme-border-light)' }}>
                    {['Table', 'Orders', 'Settled', 'Open', 'Amount'].map((h) => (
                      <th key={h} style={{ padding: '0.45rem 0.65rem', textAlign: h === 'Amount' ? 'right' : 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid var(--theme-border)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableWise.map((row, i) => (
                    <tr key={row.table_name} style={{ background: i % 2 === 0 ? '#fff' : 'var(--theme-bg-light)', borderBottom: '1px solid var(--theme-border-light)' }}>
                      <td style={{ padding: '0.45rem 0.65rem', fontWeight: 600, color: '#1e293b' }}>{row.table_name}</td>
                      <td style={{ padding: '0.45rem 0.65rem', color: '#475569' }}>{fmtNum(row.orders_count)}</td>
                      <td style={{ padding: '0.45rem 0.65rem' }}>
                        <span style={{ color: '#15803d', fontWeight: 600 }}>{fmtNum(row.settled_count || 0)}</span>
                      </td>
                      <td style={{ padding: '0.45rem 0.65rem' }}>
                        <span style={{ color: row.open_count > 0 ? 'var(--theme-warning)' : 'var(--theme-text-muted)', fontWeight: row.open_count > 0 ? 600 : 400 }}>{fmtNum(row.open_count || 0)}</span>
                      </td>
                      <td style={{ padding: '0.45rem 0.65rem', textAlign: 'right', fontWeight: 600 }}>{fmtMoney(row.order_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Low Stock Alert ── */}
      {lowStock.length > 0 && (
        <div style={card}>
          <h3 style={{ ...secHead, color: 'var(--theme-warning)' }}>⚠ Low Stock Alert ({lowStock.length} items)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.6rem' }}>
            {lowStock.map((item) => (
              <div key={item.item_code} style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.55rem 0.8rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#92400e', marginBottom: '0.2rem' }}>{item.item_code}</div>
                <div style={{ fontSize: '0.75rem', color: '#b45309' }}>
                  Available: {fmtNum(item.available_quantity_bottles)} bottles &nbsp;/&nbsp; {fmtNum(item.available_quantity_ml)} ml
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--theme-warning)', marginTop: '0.15rem' }}>
                  Reorder at: {fmtNum(item.reorder_level)} bottles
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-muted)', textAlign: 'center', marginTop: '1.25rem' }}>
        Bar Dashboard &nbsp;·&nbsp; All amounts in INR &nbsp;·&nbsp; Forecast uses {stats.forecast?.lookbackDays || 28}-day weekday average model
      </div>
    </div>
  );
}

export default Dashboard;
